/**
 * Full-page view for essay suggestions.
 *
 * Called by:
 * - App.tsx as route for /reviewer/essays/:slug/suggestions
 */
import { useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Button, Space, Spin, Tag } from 'antd';
import { ArrowLeftOutlined, BulbOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getEssay, getSuggestions } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import SuggestionDisplay from '../components/SuggestionDisplay';
import type { Essay } from '@compendium/shared';

const { Title, Text } = Typography;

export default function EssaySuggestionsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: essay, isLoading: essayLoading } = useQuery({
    queryKey: ['essay', slug],
    queryFn: () => getEssay(slug!),
    enabled: !!slug,
  });

  const { data: suggestions = [], isLoading: suggestionsLoading } = useQuery({
    queryKey: ['suggestions', slug],
    queryFn: () => getSuggestions(slug!),
    enabled: !!slug && !!user,
  });

  const handleSuggestionsChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['suggestions', slug] });
  }, [queryClient, slug]);

  const handleEssayUpdate = useCallback(
    (updatedEssay: Essay) => {
      queryClient.setQueryData(['essay', slug], (old: Essay | undefined) => {
        if (!old) return old;
        return { ...old, content: updatedEssay.content, updatedAt: updatedEssay.updatedAt };
      });
    },
    [queryClient, slug]
  );

  const pendingCount = suggestions.filter((s) => s.status === 'pending').length;

  if (essayLoading || suggestionsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link to={`/reviewer/essays/${slug}`}>
          <Button type="text" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0, marginBottom: 8 }}>
            Back to Essay
          </Button>
        </Link>
        <Space>
          <BulbOutlined style={{ fontSize: 20, color: '#d4af37' }} />
          <Title level={3} style={{ margin: 0 }}>Suggestions</Title>
          {pendingCount > 0 && (
            <Tag color="processing">{pendingCount} pending</Tag>
          )}
        </Space>
        {essay && (
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            {essay.title}
          </Text>
        )}
      </div>

      {/* Suggestions panel */}
      <SuggestionDisplay
        suggestions={suggestions}
        currentUser={user}
        onSuggestionsChange={handleSuggestionsChange}
        onEssayUpdate={handleEssayUpdate}
      />
    </div>
  );
}
